// frontend/src/components/StoryCoverCard.js
// Build: 2026-06-16-exact-flat-cover-paths-v5
//
// Purpose:
// - Shared Storylines cover renderer for uploaded, known local, and fallback covers.
// - Uses one clean rectangular image frame controlled by Storylines.css.
// - Published books prefer exact known flat cover paths before saved/Amazon cover URLs.
// - This prevents published cards from reverting to Amazon 3D mockups when flat assets exist.

import React, { useEffect, useMemo, useState } from "react";

const DEFAULT_STORYLINE_COVER = "/images/default-covers/storyline-en.webp";
const DEFAULT_SHORT_COVER = "/images/default-covers/short-story-en.webp";

function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[-–—]/g, " ")
    .replace(/\s+/g, " ");
}

function uniqueList(values) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const s = safeStr(value).trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function normalizePublicationStatus(value) {
  const raw = normalizeText(value).replace(/[\s_]+/g, "");
  if (
    raw === "publishedamazon" ||
    raw === "publishedonamazon" ||
    raw === "published" ||
    raw === "publishedexternal"
  ) {
    return "published";
  }
  return "";
}

function isPublishedBook(story) {
  return Boolean(
    normalizePublicationStatus(
      story?.publicationStatus || story?.publishingStatus
    )
  );
}

function isLikely3DMockupUrl(value) {
  const url = normalizeText(value);
  return (
    url.includes("3d") ||
    url.includes("mockup") ||
    url.includes("paperback") ||
    url.includes("_sx") ||
    url.includes("_sy") ||
    url.includes("m.media amazon") ||
    url.includes("m.media-amazon")
  );
}

function getFallbackCover(story) {
  const storyType = String(story?.storyType || "").trim().toLowerCase();
  return storyType === "short" ? DEFAULT_SHORT_COVER : DEFAULT_STORYLINE_COVER;
}

function getKnownFlatCoverCandidates(story) {
  const title = normalizeText(story?.title || story?.displayTitle || "");
  const candidates = [];

  if (title.includes("jar lingo")) {
    candidates.push(
      "/images/covers/king-jar-lingo-evel-broda-cover.jpg",
      "/images/covers/king-jar-lingo-evel-broda-cover.webp"
    );
  }

  if (title.includes("didie")) {
    candidates.push(
      "/images/covers/didie-cover.jpg",
      "/images/covers/didie-cover.webp"
    );
  }

  if (
    title.includes("réflexions du voyageur") ||
    title.includes("reflexions du voyageur") ||
    title.includes("réflexions d'un voyageur") ||
    title.includes("reflexions d'un voyageur")
  ) {
    candidates.push(
      "/images/covers/reflexions-du-voyageur-fr.webp",
      "/images/covers/reflexions-du-voyageur-fr.jpg"
    );
  }

  if (title.includes("reflections of the wayfarer") || title.includes("reflections of a wayfarer")) {
    candidates.push(
      "/images/covers/reflections-of-the-wayfarer-en.webp",
      "/images/covers/reflections-of-the-wayfarer-en.jpg"
    );
  }

  // Exact flat files confirmed by `find public`.
  if (title.includes("ugo and the butterfly woman")) {
    candidates.push("/images/covers/ugo-butterfly-woman.webp");
  }

  if (title.includes("ugo et la femme papillon") || title.includes("ugo et la femme papillon")) {
    candidates.push("/images/covers/ugo-femme-papillon.webp");
  }

  if (title.includes("ugo and her friend") || title.includes("ugo and her friend, awa")) {
    candidates.push(
      "/images/covers/featured-flat/ugo-and-her-friend-awa.webp",
      "/images/covers/ugo-and-her-friend-awa.webp"
    );
  }

  if (title.includes("ugo and her pet dog")) {
    candidates.push(
      "/images/covers/featured-flat/ugo-her-pet-dog.webp",
      "/images/covers/ugo-and-her-pet-dog.webp"
    );
  }

  if (title.includes("ugo et son petit chien")) {
    candidates.push(
      "/images/covers/featured-flat/ugo-et-son-petit-chien.webp",
      "/images/covers/ugo-et-son-petit-chien.webp"
    );
  }

  return uniqueList(candidates);
}

function getExternalCoverCandidates(story) {
  const publicationCover = safeStr(
    story?.publicationCoverUrl ||
      story?.publishedCoverUrl ||
      story?.finalCoverImageUrl
  ).trim();

  const uploadedCover = safeStr(
    story?.coverImageUrl ||
      story?.coverUrl ||
      story?.coverImage ||
      story?.imageUrl
  ).trim();

  const nonMockup = [];
  const mockupOrUnknown = [];

  [publicationCover, uploadedCover].forEach((url) => {
    if (!url) return;
    if (isLikely3DMockupUrl(url)) mockupOrUnknown.push(url);
    else nonMockup.push(url);
  });

  return { nonMockup: uniqueList(nonMockup), mockupOrUnknown: uniqueList(mockupOrUnknown) };
}

function getStoryCoverCandidates(story, preferWorkingCover = false) {
  const knownFlat = getKnownFlatCoverCandidates(story);
  const external = getExternalCoverCandidates(story);
  const fallback = getFallbackCover(story);

  const workingCover = safeStr(
    story?.coverImageUrl ||
      story?.coverUrl ||
      story?.coverImage ||
      story?.imageUrl
  ).trim();

  const publicationCover = safeStr(
    story?.publicationCoverUrl ||
      story?.publishedCoverUrl ||
      story?.finalCoverImageUrl
  ).trim();

  // Creator workspaces must show the Writer's current working cover,
  // even when an older official publication cover also exists.
  if (preferWorkingCover) {
    return uniqueList([
      workingCover,
      publicationCover,
      ...knownFlat,
      ...external.nonMockup,
      ...external.mockupOrUnknown,
      fallback,
    ]);
  }

  if (isPublishedBook(story)) {
    const explicitPublicationCover = safeStr(
      story?.publicationCoverUrl ||
        story?.publishedCoverUrl ||
        story?.finalCoverImageUrl
    ).trim();

    if (explicitPublicationCover) {
      return uniqueList([
        explicitPublicationCover,
        ...knownFlat,
        fallback,
      ]);
    }

    if (knownFlat.length) {
      return uniqueList([
        ...knownFlat,
        ...external.nonMockup,
        fallback,
      ]);
    }

    return uniqueList([
      ...external.nonMockup,
      fallback,
    ]);
  }

  return uniqueList([
    ...external.nonMockup,
    ...external.mockupOrUnknown,
    fallback,
  ]);
}

export default function StoryCoverCard({
  story,
  preferWorkingCover = false,
}) {
  const title = String(story?.title || story?.displayTitle || "").trim();
  const candidates = useMemo(
    () => getStoryCoverCandidates(story, preferWorkingCover),
    [story, preferWorkingCover]
  );
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [candidates.join("|")]);

  const coverUrl = candidates[candidateIndex] || getFallbackCover(story);

  return (
    <div
      className="storylines-cover-card"
      role={title ? "img" : undefined}
      aria-label={title ? `${title} cover` : undefined}
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt={title || "Story cover"}
          className="storylines-cover-bg"
          loading="lazy"
          draggable="false"
          onError={(e) => {
            if (candidateIndex < candidates.length - 1) {
              setCandidateIndex((prev) => prev + 1);
              return;
            }
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="storylines-cover-bg storylines-cover-bg-fallback" aria-hidden="true" />
      )}

      <div className="storylines-cover-overlay" aria-hidden="true" />
    </div>
  );
}
